import React, { useMemo } from "react";
import { SectionTitle } from ".";
import useAutoSave from "../useAutoSave";
import { Input, Select } from "../../../UI";
import useEpmValidation from "../../validation/useEpmValidation";
import { useEPMContext } from "../../../../contexts/EPMContext";
import { useUserContext } from "../../../../contexts/UserContext";
import { permission } from "../../../../config/permission";
import { EPM_LIMITS, testPattern } from "../../validation/epmRules";

const LocationSection = ({ testData, setTestData }) => {
    const { testLocations = [] } = useEPMContext();
    const { checkPermission } = useUserContext();
    const canUpdate = checkPermission(permission.XCM_TESTS_UPDATE);
    const { saveNow, saveLater } = useAutoSave(testData);
    const { fieldErrors, validateFieldValue, clearFieldError } = useEpmValidation();

    // Consume testLocations sorted ascending by order
    const sortedLocations = useMemo(() => {
        return [...(testLocations || [])].sort(
            (a, b) => (Number(a?.order) || 0) - (Number(b?.order) || 0),
        );
    }, [testLocations]);

    const locationValues =
        testData?.ideation?.location || testData?.location || {};

    const validateLocationFieldFormat = (loc, value) => {
        if (!value) return null;
        if (loc.field_type === "dropdown") {
            if (value && !(loc.field_options || []).includes(value)) {
                return `Must be one of the configured options.`;
            }
        } else {
            const str = String(value).trim();
            if (str.length > EPM_LIMITS.LOCATION_TEXT_MAX) {
                return `Must be ${EPM_LIMITS.LOCATION_TEXT_MAX} characters or fewer (${str.length}/${EPM_LIMITS.LOCATION_TEXT_MAX}).`;
            }
            if (loc.validation_pattern && !testPattern(loc.validation_pattern, str)) {
                return `Does not match the required pattern for ${loc.label}.`;
            }
        }
        return null;
    };

    // Text fields save once typing pauses, dropdowns straight away
    const handleFieldChange = (location, value, { debounced = false } = {}) => {
        if (!canUpdate || !setTestData) return;
        const key = location.key;

        const formatErr = validateLocationFieldFormat(location, value);
        if (formatErr) {
            validateFieldValue(key, value, () => formatErr);
        } else {
            clearFieldError(key);
        }

        setTestData((prev) => {
            const currentIdeation = prev?.ideation || {};
            const currentLocation =
                currentIdeation.location || prev?.location || {};

            return {
                ...prev,
                ideation: {
                    ...currentIdeation,
                    location: {
                        ...currentLocation,
                        [key]: value,
                    },
                },
            };
        });

        if (debounced) {
            saveLater(
                "ideation.location",
                (test) => ({ ideation: { location: test?.ideation?.location || {} } }),
                (test) => validateLocationFieldFormat(location, test?.ideation?.location?.[key])
            );
        } else {
            if (!formatErr) {
                saveNow({ ideation: { location: { ...locationValues, [key]: value } } }, "ideation.location");
            }
        }
    };

    return (
        <section className="epm-idea__card">
            <SectionTitle>Location</SectionTitle>
            <div className="epm-idea__location-grid">
                {sortedLocations.map((location) => {
                    const fieldKey = location.key;
                    const fieldValue = locationValues[fieldKey] ?? "";
                    const err = fieldErrors[fieldKey];

                    return (
                        <div key={location._id || location.key} className="flex flex-col gap-1">
                            <label className="flex items-center gap-4">
                                <span className="w-[150px] flex items-center gap-1.5 stride-form-label !mb-0 flex-none">
                                    {location.label}
                                    {location.required && (
                                        <span className="text-red-500 font-bold ml-0.5">
                                            *
                                        </span>
                                    )}
                                    {location?.conflict_role !== "None" && (
                                        <span className="bg-primary rounded-full text-white px-2 py-[2px] text-[10px]">
                                            {location.conflict_role}
                                        </span>
                                    )}
                                </span>

                                <div className="flex-1 min-w-0" id={`epm-field-location-${location.key}`}>
                                    {location.field_type === "dropdown" ? (
                                        <Select
                                            id={`select-${fieldKey}`}
                                            value={fieldValue}
                                            placeholder={`Select ${location.label}`}
                                            disabled={!canUpdate}
                                            options={(location.field_options || []).map((option) => ({
                                                value: option,
                                                label: option,
                                            }))}
                                            handleChange={(e) =>
                                                handleFieldChange(
                                                    location,
                                                    e.target.value,
                                                )
                                            }
                                            className="m-0 w-full max-w-[260px]"
                                            mandatory={location.required}
                                        />
                                    ) : (
                                        <Input
                                            type="text"
                                            disabled={!canUpdate}
                                            maxLength={EPM_LIMITS.LOCATION_TEXT_MAX}
                                            placeholder={`Enter ${location.label.toLowerCase()}...`}
                                            value={fieldValue}
                                            onChange={(e) =>
                                                handleFieldChange(
                                                    location,
                                                    e.target.value,
                                                    { debounced: true },
                                                )
                                            }
                                            aria-label={location.label}
                                            error={err}
                                            className="w-full"
                                        />
                                    )}
                                </div>
                            </label>
                            {err && (
                                <div className="pl-[166px] epm-field-error">
                                    {err}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </section>
    );
};

export default LocationSection;
